import React, { useRef, useState, useMemo, useEffect, useLayoutEffect } from 'react'
import CommonLayout from 'layouts/DashboardLayout'
import {
    Box,
    Grid,
    Stack,
    Switch,
    Typography,
    IconButton,
    Card,
    Button,
    TextField,
    InputAdornment
} from "@mui/material";
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { CloseRounded, RemoveRedEye } from '@mui/icons-material';
import MDButton from 'components/MDButton';
import { AgGridTable } from 'components/tables/AgGridTable';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { closeCustomModalDialog, openCustomModalDialog, setModalLoader } from '../../../redux/slices/modalSlice';
import EditIcon from 'common/customIcons/edit';
import DeleteIcon from 'common/customIcons/delete';
import { deleteProductCarousel, fetchProductCarousels, updateCarouselsStatusApi } from '../../../redux/slices/products';
import { useSelector } from 'react-redux';
import { getPermissionsByMode } from 'utilities/common';
import { hasSectionPermission } from 'utilities/common';
import { getData } from 'utilities/common';
import { openSnackbar } from '../../../redux/slices/common';
import SearchIcon from 'common/customIcons/search';
import { useDebounce } from 'hooks/useDebounce';

const ProductCarousels = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const pageRef = useRef(0);
    const mainGridApiRef = useRef('');
    const gridRefApi = useRef(null);
    const { productCarousels = {}, fetchProductCarouselsLoading: loading = false } = useSelector(state => state.products);
    // Active tab state
    const searchTermRef = useRef('');
    const [searchParams, setSearchParams] = useSearchParams();
    // Check both 'platform' and 'roleTab' for backward compatibility
    const fullRoleTab = useMemo(() => searchParams.get('platform') || searchParams.get('roleTab'), [searchParams]);
    const [searchTerm, setSearchTerm] = useState("");

    useLayoutEffect(() => {
        // Only set default if no platform is specified at all
        if (!fullRoleTab) {
            setSearchParams({ platform: 'Hyperwolf' });
        } else if (searchParams.get('roleTab') && !searchParams.get('platform')) {
            // If roleTab exists but platform doesn't, migrate to platform and remove roleTab
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('roleTab');
            newParams.set('platform', fullRoleTab);
            setSearchParams(newParams);
        }
    }, [fullRoleTab, setSearchParams, searchParams])

    // Handle status toggle
    const handleStatusToggle = (data) => {

        const payload = {
            "status": data?.status === 'inactive' ? "active" : "inactive",
        }
        dispatch(updateCarouselsStatusApi({ id: data?._id, data: payload }))
            .unwrap()
            .then(res => {
                searchFn()

                showError(res?.message, 'success');
            })
            .catch(err => {
                showError(err?.message, 'error');
            });
    };

    // Handle view action
    const handleView = (props) => {
        // Add your view logic here
        navigate(`/hyperwolf/product-carousels/add?platform=${fullRoleTab}&carouselId=${props?.data?._id}&view=true`);
    };

    // Handle edit action
    const handleEdit = (props) => {
        navigate(`/hyperwolf/product-carousels/add?platform=${fullRoleTab}&carouselId=${props?.data?._id}`);
        // Add your edit logic here
    };

    // Handle delete action
    const deleteHandler = (carouselId) => {
        dispatch(setModalLoader(true));
        dispatch(deleteProductCarousel(carouselId)).unwrap().then((res) => {
            showError(res?.message, 'success');
            dispatch(closeCustomModalDialog());
            pageRef.current = 0;
            const dataSource = {
                getRows
            };
            mainGridApiRef.current.ensureIndexVisible(0, null);
            mainGridApiRef.current.setGridOption('datasource', dataSource);
        }).catch((err) => {
            dispatch(setModalLoader(false));
            showError(err?.message, 'error');
        });
    };
    const showError = (message, errortype) => {
        if (errortype === 'success') {
            dispatch(openSnackbar({ content: message, color: 'success', icon: 'check' }));
        }
        if (errortype === 'error') {
            dispatch(openSnackbar({ content: message, color: 'error', icon: 'warning' }));
        }
    };

    // Column definitions
    const columnDefs = [
        {
            headerName: 'Name',
            field: 'title',
            suppressMenu: false,
            flex: 1.5,
            minWidth: 180,
        },
        {
            headerName: 'Placement',
            field: 'placement',
            suppressMenu: false,
            flex: 3,
            minWidth: 240,
        },
        {
            headerName: 'Total Products Count',
            field: 'productsCount',
            suppressMenu: false,
            flex: 1,
            minWidth: 180,
            maxWidth: 240,
            cellRenderer: (props) => {
                return props.value || 0;
            },
        },
        {
            headerName: 'Status',
            field: 'status',
            suppressMenu: false,
            flex: 1,
            minWidth: 100,
            maxWidth: 140,
            cellRenderer: (props) => {
                const handleToggle = (e) => {
                    e.stopPropagation();
                    handleStatusToggle(props.data);
                };

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Switch
                            checked={props?.data?.status === "active" ? true : false}
                            onChange={handleToggle}
                            color={props?.data?.status ? "success" : "default"}
                        />
                    </Box>
                );
            },
        },
        {
            headerName: 'Actions',
            sortable: false,
            suppressMenu: true,
            cellRenderer: (props) => {
                return (
                    <Box display="inline-flex" gap={1} alignItems="center">
                        <IconButton
                            disabled={!canView}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleView(props);
                            }}
                            color="primary"
                            size="small"
                            variant="primary"
                        >
                            <RemoveRedEye />
                        </IconButton>
                        <IconButton
                            disabled={!canEdit}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(props);
                            }}
                            color="primary"
                            size="small"
                            variant="primary"
                        >
                            <EditIcon width="16px" height="16px" />
                        </IconButton>
                        <IconButton
                            disabled={!canDelete}
                            onClick={(e) => {
                                e.stopPropagation();
                                dispatch(
                                    openCustomModalDialog({
                                        modalData: {
                                            deleteHandler,
                                            deleteId: props.data?._id,
                                            deleteType: "Product Carousel",
                                        },
                                        modal: "DELETE_MODAL",
                                    })
                                );
                            }}
                            color="error"
                            size="small"
                            variant="primary"
                        >
                            <DeleteIcon width="16px" height="16px" />
                        </IconButton>
                    </Box>
                );
            },
            cellStyle: { textAlign: "right", justifyContent: "end" },
            headerClass: "right-aligned-cell-header",
            minWidth: 120,
            maxWidth: 150,
            flex: 1,
            pinned: "right",
        },
    ];

    function getSortInfo(data) {
        const sortInfo = {
            sortBy: '',
            sortOrder: ''
        };
        data.forEach(item => {
            sortInfo.sortBy = item.colId;
            sortInfo.sortOrder = item.sort === "asc" ? 'asc' : 'desc';
        });
        return sortInfo;
    }

    const getRows = async (params) => {

        gridRefApi.current = params;
        let filter = {};
        if (params?.sortModel.length) {
            filter = { ...getSortInfo(params?.sortModel) };
        }
        dispatch(fetchProductCarousels({ skip: pageRef.current, limit: 20, platformType: fullRoleTab || 'Hyperwolf', searchTerm: searchTermRef.current, ...filter })).unwrap().then(res => {

            params.successCallback(res?.data, res?.pagination?.total);
            pageRef.current = pageRef.current + 20;
        }).catch(() => {
            params.successCallback([], 0);
        });
    };


    useEffect(() => {
        if (fullRoleTab) {
            pageRef.current = 0;
            searchFn();
        }
    }, [fullRoleTab]);

    useEffect(() => {
        if (mainGridApiRef?.current) {
            if (loading) {
                mainGridApiRef.current.showLoadingOverlay();
            } else if (productCarousels?.pagination?.total === 0) {
                mainGridApiRef.current.showNoRowsOverlay();
            }
            else {
                mainGridApiRef.current.hideOverlay();
            }
        }

    }, [productCarousels, loading]);



    const searchHandler = (searchValue) => {
        setSearchTerm(searchValue);
        searchTermRef.current = searchValue;
        handleSearch(searchValue);
        // Debounce the search callback
    };

    const handleSearch = useDebounce(() => {
        pageRef.current = 0;
        searchFn();
    }, 500);

    const searchFn = (value) => {
        pageRef.current = 0;
        const dataSource = {
            getRows: (params) => getRows(params, value) // Pass the current filter payload
        };
        if (mainGridApiRef.current) {
            mainGridApiRef.current.ensureIndexVisible(0, null);
            mainGridApiRef.current.setGridOption("datasource", dataSource);
        }
    };

    let localUser = getData('login-user-info')
    if (localUser) {
        localUser = JSON.parse(localUser)
    }

    const adminMode = localStorage.getItem("adminsMode") ? JSON.parse(localStorage.getItem("adminsMode")) : null;
    const permissionsForMode = getPermissionsByMode(localUser?.permissions, adminMode);

    const canCreate = hasSectionPermission(permissionsForMode, "Settings", "Create");
    const canEdit = hasSectionPermission(permissionsForMode, "Settings", "Edit/Update");
    const canView = hasSectionPermission(permissionsForMode, "Settings", "View Only");
    const canDelete = hasSectionPermission(permissionsForMode, "Settings", "Delete");

    return (
        <CommonLayout>
            <Box p={{ xs: 2, sm: 3, lg: 4 }}>
                <Box mb={2} className="cannabinoids-tabs">
                    <Stack direction='row' gap={1} sx={{ background: '#ffffff' }}>
                        <Button
                            variant={fullRoleTab === 'Hyperwolf' ? 'contained' : 'outlined'}
                            onClick={() => { setSearchParams({ platform: `Hyperwolf` }); }}
                            disabled={loading}

                            sx={{
                                color: "#2E2E3A",
                                minWidth: "160px",
                                backgroundColor: fullRoleTab === 'Hyperwolf' ? '#F2F3F7' : '#ffffff',
                                '&:hover': {
                                    backgroundColor: fullRoleTab === 'Stilo' ? '#F2F3F7' : '#F2F3F7',
                                },
                                '&:focus': {
                                    backgroundColor: fullRoleTab === 'Stilo' ? '#F2F3F7' : '#F2F3F7',
                                }
                            }}
                            style={{ boxShadow: "none !important" }}
                        >
                            Hyperwolf
                        </Button>
                        <Button
                            variant={fullRoleTab === 'Hemp' ? 'contained' : 'outlined'}
                            onClick={() => { setSearchParams({ platform: `Hemp` }); }}
                            disabled={loading}

                            sx={{
                                color: "#2E2E3A",
                                minWidth: "160px",
                                backgroundColor: fullRoleTab === 'Hemp' ? '#F2F3F7' : '#ffffff',
                                '&:hover': {
                                    backgroundColor: fullRoleTab === 'Stilo' ? '#F2F3F7' : '#F2F3F7',
                                },
                                '&:focus': {
                                    backgroundColor: fullRoleTab === 'Stilo' ? '#F2F3F7' : '#F2F3F7',
                                }
                            }}
                            style={{ boxShadow: "none !important" }}
                        >
                            Hemp
                        </Button>
                        <Button
                            variant={fullRoleTab === 'Stilo' ? 'contained' : 'outlined'}
                            onClick={() => { setSearchParams({ platform: `Stilo` }); }}
                            disabled={loading}
                            sx={{
                                color: "#2E2E3A",
                                minWidth: "160px",
                                backgroundColor: fullRoleTab === 'Stilo' ? '#F2F3F7' : '#ffffff',
                                '&:hover': {
                                    backgroundColor: fullRoleTab === 'Stilo' ? '#F2F3F7' : '#F2F3F7',
                                },
                                '&:focus': {
                                    backgroundColor: fullRoleTab === 'Stilo' ? '#F2F3F7' : '#F2F3F7',
                                }
                            }}
                            style={{ boxShadow: "none !important" }}
                        >
                            Stilo
                        </Button>
                    </Stack>
                </Box>

                <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <Typography variant='h4'>Product Carousels</Typography>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <Stack direction="row" justifyContent={{ xs: 'end', sm: 'end' }} flexWrap="wrap" alignItems="stretch" gap={2}>
                            <TextField
                                className="custom-search-field"
                                placeholder="Search"
                                label=""
                                value={searchTerm}
                                onChange={(e) => searchHandler(e.target.value)}
                                sx={{ minWidth: "280px" }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IconButton size="small">
                                                <SearchIcon width="18px" height="18px" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    endAdornment:
                                        searchTerm?.length > 0 ? (
                                            <InputAdornment>
                                                <IconButton onClick={() => searchHandler("")} size="small">
                                                    <CloseRounded />
                                                </IconButton>
                                            </InputAdornment>
                                        ) : null,
                                }}
                            />
                            {
                                canCreate &&
                                <MDButton
                                    startIcon={<AddRoundedIcon fontSize='large' />}
                                    variant="contained"
                                    color="primary"
                                    sx={{ minWidth: "170px" }}
                                    onClick={() => {
                                        navigate(`/hyperwolf/product-carousels/add?platform=${fullRoleTab}`);
                                    }}
                                >
                                    Add New Carousel
                                </MDButton>
                            }
                        </Stack>
                    </Grid>
                </Grid>

                {
                    canView &&
                    <Box mt={2}>
                        <AgGridTable
                            col={columnDefs}
                            getRows={getRows}
                            mainGridApiRef={mainGridApiRef}
                            pageRef={pageRef}
                            // isLoading={loading}
                            style={{ height: "70vh" }}
                        />

                    </Box>
                }
            </Box>
        </CommonLayout>
    )
}

export default ProductCarousels
